import React, { useRef, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { toast } from "react-toastify";
import {
  ACCENT, api, errorMessage, ROLES, GENRES, COUNTRIES, DIAL_CODES, Field, Chip, PrimaryButton, SecondaryButton,
  inputClass, ringStyle, ID_TYPE_OPTIONS, PROFILE_LINK_FIELDS, clean, filtered,
} from "./shared";
import { ProfileImageUploader } from "./uploaders";
import DocumentSlot from "./DocumentSlot";

const STEPS = ["Application", "Verification", "Payout Setup"];
const LAST_STEP = STEPS.length - 1;
const LINK_KEYS = PROFILE_LINK_FIELDS.map(([key]) => key);
const STEP_FIELDS = [
  ["artist_name", "date_of_birth", "country", "email", "mobile_number", "profile_link", "bio"],
  ["verification", "distributor_reference", ...LINK_KEYS],
  ["legal_name", "id_type", "id_front", "id_back", "account_holder_name", "payout_account", "age_verified", "terms_accepted"],
];

function isAtLeast18(dateOfBirth) {
  if (!dateOfBirth) return false;
  const dob = new Date(dateOfBirth);
  if (Number.isNaN(dob.getTime()) || dob.getTime() > Date.now()) return false;
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const m = today.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age -= 1;
  return age >= 18;
}

// API field name -> wizard field name (the API takes one combined phone number).
const SERVER_FIELD_MAP = {
  contact_number: "mobile_number",
  _form: "verification",
  distributor_reference: "distributor_reference",
  id_document_urls: "id_front",
  id_document_hashes: "id_front",
  supporting_document_urls: "id_back",
  ...Object.fromEntries(LINK_KEYS.map((k) => [`verification_links.${k}`, "verification"])),
};

const isUrl = (v) => /^https?:\/\/\S+\.\S+/.test(v);
const idTypeInfo = (value) => ID_TYPE_OPTIONS.find((o) => o.value === value);

// "+15550001111" -> ["+1", "5550001111"] using the dial codes the form offers (longest match wins).
function splitPhone(contactNumber) {
  const value = String(contactNumber || "");
  const code = [...DIAL_CODES].sort((a, b) => b.length - a.length).find((d) => value.startsWith(d));
  return code ? [code, value.slice(code.length)] : ["+1", value.replace(/^\+/, "")];
}

// Starting values: blank for a new application, or the previous (rejected) application's details for
// a resubmission. Documents and the payout account number are never pre-filled: rejected documents
// must be replaced, and the account number is never sent back by the API.
function startingValues(initial, storedUser) {
  const blank = {
    artist_name: "", date_of_birth: "", country: "", email: storedUser?.email || "",
    dial_code: "+1", mobile_number: "", primary_role: "musician", genres: [],
    profile_link: "", bio: "", profile_image_url: "", distributor_reference: "",
    legal_name: "", id_type: "", id_front: null, id_back: null, supporting_docs: [],
    tax_form: "w9", payout_method: "bank_transfer", account_holder_name: "",
    payout_account: "",
    age_verified: false, terms_accepted: false,
    ...Object.fromEntries(LINK_KEYS.map((k) => [k, ""])),
  };
  if (!initial) return blank;
  const [dial, mobile] = splitPhone(initial.contact_number);
  const links = initial.verification_links || {};
  return {
    ...blank,
    artist_name: initial.artist_name || "",
    date_of_birth: initial.date_of_birth ? String(initial.date_of_birth).slice(0, 10) : "",
    country: initial.country || "",
    email: initial.email || blank.email,
    dial_code: dial,
    mobile_number: mobile,
    primary_role: initial.primary_role || "musician",
    genres: initial.genres || [],
    profile_link: initial.profile_link || "",
    bio: initial.bio || "",
    profile_image_url: initial.profile_image_url || "",
    distributor_reference: initial.distributor_reference || "",
    legal_name: initial.legal_name || "",
    id_type: ID_TYPE_OPTIONS.some((o) => o.value === initial.id_type) ? initial.id_type : "",
    tax_form: initial.tax_form || "w9",
    payout_method: initial.payout_method === "paypal" ? "paypal" : "bank_transfer",
    account_holder_name: initial.account_holder_name || "",
    ...Object.fromEntries(LINK_KEYS.map((k) => [k, links[k] || ""])),
  };
}

// The three verification providers shown as cards: [field, title, subtitle, link placeholder].
const VERIFICATION_PROVIDERS = [
  ["spotify", "Spotify for Artists", "Verify ownership of your Spotify artist profile", "https://open.spotify.com/artist/..."],
  ["youtube", "YouTube", "Verify ownership of your YouTube channel", "https://youtube.com/@yourchannel"],
  ["instagram", "Instagram", "Verify ownership of your Instagram profile", "https://instagram.com/yourhandle"],
];

// One provider card: title and subtitle, and a Connect button that opens the link field. A valid link shows "Connected".
function ProviderCard({ title, subtitle, placeholder, registration, value, error }) {
  const [open, setOpen] = useState(false);
  const connected = !!value && isUrl(String(value).trim()) && !error;
  const showInput = open || (!!value && !connected) || !!error;
  return (
    <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-bold text-gray-900">{title}</p>
          <p className="text-sm text-gray-500">{subtitle}</p>
        </div>
        {connected && !open ? (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="shrink-0 rounded-full bg-green-50 px-4 py-1.5 text-sm font-semibold"
            style={{ color: ACCENT }}
          >
            Connected ✓
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="shrink-0 rounded-full border border-gray-300 bg-white px-4 py-1.5 text-sm font-semibold text-gray-900 hover:bg-gray-50"
          >
            {open ? "Done" : "Connect"}
          </button>
        )}
      </div>
      <div className={showInput ? "mt-3" : "hidden"}>
        <input
          type="url"
          className={inputClass(error)}
          style={ringStyle}
          placeholder={placeholder}
          aria-label={`${title} link`}
          {...registration}
        />
        {error && <p className="text-red-600 text-sm mt-1">{error.message}</p>}
      </div>
    </div>
  );
}

function ConfirmDialog({ open, busy, onCancel, onConfirm }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex [align-items:safe_center] justify-center overflow-y-auto bg-black/40 px-4 py-4" role="presentation">
      <div role="dialog" aria-modal="true" aria-labelledby="confirm-title" className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
        <h2 id="confirm-title" className="text-xl font-bold text-gray-900 mb-2">Submit application?</h2>
        <p className="text-gray-600 mb-6">Are you sure you want to submit your artist application?</p>
        <div className="flex justify-end gap-3">
          <SecondaryButton onClick={onCancel} disabled={busy}>Go back</SecondaryButton>
          <PrimaryButton onClick={onConfirm} disabled={busy}>{busy ? "Submitting..." : "Yes, submit"}</PrimaryButton>
        </div>
      </div>
    </div>
  );
}

export default function ApplicationWizard({ onSubmitted, onCancel, initial = null }) {
  const storedUser = JSON.parse(sessionStorage.getItem("user") || localStorage.getItem("user") || "null");
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const topRef = useRef(null);

  const {
    register, control, handleSubmit, trigger, watch, setValue, getValues, setError, clearErrors,
    formState: { errors },
  } = useForm({ mode: "onChange", defaultValues: startingValues(initial, storedUser) });
  // Same as register(), but the text is cleaned as it is typed or pasted (digits only for numbers, letters for names).
  const filteredReg = (name, fn, max, options) => filtered(register(name, options), fn, max);

  const values = watch();
  const idInfo = idTypeInfo(values.id_type);
  const stepHasErrors = (i) => STEP_FIELDS[i].some((f) => errors[f]);
  const currentStepHasErrors = stepHasErrors(step);
  const errorStepIndex = STEPS.findIndex((_, i) => stepHasErrors(i));
  const anyErrors = errorStepIndex !== -1;
  const genres = values.genres;

  const scrollTop = () => topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  // Step 2 rule: at least one valid official profile link, or a distributor reference code.
  const verificationProblem = () => {
    const v = getValues();
    const links = LINK_KEYS.map((k) => (v[k] || "").trim());
    const hasRef = (v.distributor_reference || "").trim();
    // A reference of only spaces counts as empty: it passes when a profile link is given, otherwise this message shows.
    if (!links.some(Boolean) && !hasRef) {
      return "Add at least one official profile link (Spotify, YouTube, Instagram, Apple Music, website…) or a distributor reference code.";
    }
    if (links.some((x) => x && !isUrl(x))) return "Profile links must start with http:// or https://.";
    return null;
  };

  const checkVerificationStep = async () => {
    const linksOk = await trigger([...LINK_KEYS, "distributor_reference"]);
    const problem = verificationProblem();
    if (problem) setError("verification", { message: problem });
    else clearErrors("verification");
    return linksOk && !problem;
  };

  const checkStep = async (i) => {
    if (i === 1) return checkVerificationStep();
    return trigger(STEP_FIELDS[i]);
  };

  // Before submitting: find every step with missing / invalid mandatory information.
  const collectIssues = async () => {
    const bad = [];
    for (let i = 0; i <= LAST_STEP; i += 1) {
      // eslint-disable-next-line no-await-in-loop
      if (!(await checkStep(i))) bad.push(i);
    }
    return bad;
  };

  const goNext = async () => {
    if (!(await checkStep(step))) return;
    setStep((s) => Math.min(s + 1, LAST_STEP));
    scrollTop();
  };

  // The last step's button: check every step, jump to the first one with a problem, otherwise ask to confirm.
  const requestSubmit = async () => {
    const bad = await collectIssues();
    if (bad.length) {
      goTo(bad[0]);
      return;
    }
    setConfirmOpen(true);
  };

  const goTo = (i) => {
    setStep(i);
    scrollTop();
  };

  const onSubmit = async (data) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const links = {};
      LINK_KEYS.forEach((k) => {
        if (data[k] && data[k].trim()) links[k] = data[k].trim();
      });
      const idFiles = [data.id_front, data.id_back].filter(Boolean);
      const hashes = idFiles.map((f) => f.hash);
      const response = await api.post("submit", {
        artist_name: data.artist_name.trim(),
        email: data.email.trim(),
        contact_number: `${data.dial_code}${data.mobile_number.replace(/\D/g, "")}`,
        date_of_birth: data.date_of_birth,
        age_verified: !!data.age_verified,
        country: data.country,
        primary_role: data.primary_role,
        genres: data.genres,
        profile_link: data.profile_link.trim() || undefined,
        bio: data.bio.trim() || undefined,
        profile_image_url: data.profile_image_url || undefined,
        verification_links: links,
        distributor_reference: data.distributor_reference.trim() || undefined,
        legal_name: data.legal_name.trim(),
        id_type: data.id_type,
        id_document_urls: idFiles.map((f) => f.url),
        id_document_hashes: hashes.every(Boolean) ? hashes : undefined,
        supporting_document_urls: data.supporting_docs.length ? data.supporting_docs.map((d) => d.url) : undefined,
        tax_form: data.tax_form,
        payout_method: data.payout_method,
        account_holder_name: data.account_holder_name.trim(),
        payout_account: data.payout_account.trim(),
        terms_accepted: !!data.terms_accepted,
      });
      if (response.data?.success) {
        if (!response.data.alreadySubmitted) toast.success("Your artist application has been submitted.");
        onSubmitted(response.data.result);
      }
    } catch (error) {
      const status = error?.response?.status;
      const serverErrors = error?.response?.data?.errors;
      if (status === 400 && serverErrors && typeof serverErrors === "object") {
        // Show each API validation error next to its field, and go to the first step that has one.
        const applied = {};
        Object.entries(serverErrors).forEach(([key, message]) => {
          const field = SERVER_FIELD_MAP[key] || key;
          setError(field, { type: "server", message });
          applied[field] = true;
        });
        onInvalid(applied);
        toast.error("Please fix the highlighted fields and try again.");
      } else if (status === 409) {
        const message = errorMessage(error, "This application could not be submitted.");
        if (/document/i.test(message)) {
          setError("id_front", { type: "server", message });
          setStep(2);
        }
        toast.error(message);
      } else {
        toast.error(errorMessage(error, "We couldn't submit your application right now. Please try again."));
      }
    } finally {
      setSubmitting(false);
      setConfirmOpen(false);
    }
  };

  // If final validation fails on a field from an earlier step, take the person back to it.
  const onInvalid = (formErrors) => {
    const firstBad = STEP_FIELDS.findIndex((fields) => fields.some((f) => formErrors[f]));
    if (firstBad !== -1 && firstBad !== step) setStep(firstBad);
    setConfirmOpen(false);
  };

  const toggleGenre = (g) => {
    const current = getValues("genres");
    if (current.includes(g)) setValue("genres", current.filter((x) => x !== g));
    else if (current.length < 8) setValue("genres", [...current, g]);
  };

  const hashesExcept = (...slots) => slots.filter(Boolean).map((s) => s.hash).filter(Boolean);

  return (
    <div ref={topRef}>
      <h1 className="text-[28px] leading-tight font-bold text-gray-900 mb-4">{initial ? "Update and resubmit your application" : "Become an Artist"}</h1>

      {initial?.rejection_reason && (
        <div className="mb-5 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-sm px-4 py-3">
          <p className="font-semibold">Why your last submission was not approved</p>
          <p className="mt-1 whitespace-pre-line">{initial.rejection_reason}</p>
          <p className="mt-2">Please correct the information below and replace your documents, then submit again.</p>
        </div>
      )}

      <ol className="flex flex-wrap gap-x-6 gap-y-1 mb-6 text-sm font-semibold" aria-label="Application steps">
        {STEPS.map((label, i) => (
          <li
            key={label}
            aria-current={i === step ? "step" : undefined}
            className={i === step ? "" : "text-gray-400"}
            style={i === step ? { color: ACCENT } : undefined}
          >
            {label}
          </li>
        ))}
      </ol>

      <form
        onSubmit={(e) => e.preventDefault()}
        onKeyDown={(e) => {
          // Enter inside a single-line field should advance the wizard, never submit a half-filled form.
          if (e.key === "Enter" && e.target.tagName !== "TEXTAREA") {
            e.preventDefault();
            if (step < LAST_STEP) goNext();
          }
        }}
        noValidate
        className="space-y-5"
      >
        {/* ---------- Step 1: Basic information ---------- */}
        <div className={step === 0 ? "space-y-5" : "hidden"}>
          <Field label="Artist profile image" hint="Shown on your public artist profile.">
            <Controller
              name="profile_image_url"
              control={control}
              render={({ field }) => <ProfileImageUploader value={field.value} onChange={field.onChange} />}
            />
          </Field>

          <Field label="Artist / stage name" required error={errors.artist_name?.message}>
            <input
              type="text"
              className={inputClass(errors.artist_name)}
              style={ringStyle}
              placeholder="e.g. Nova Reyes"
              {...register("artist_name", {
                required: "Artist name is required.",
                minLength: { value: 2, message: "Artist name must be at least 2 characters." },
                maxLength: { value: 150, message: "Artist name must be at most 150 characters." },
              })}
            />
          </Field>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field
              label="Date of birth"
              required
              hint="You must be 18 or older to join Artist Studio."
              error={errors.date_of_birth?.message}
            >
              <input
                type="date"
                max={new Date().toISOString().slice(0, 10)}
                className={inputClass(errors.date_of_birth)}
                style={ringStyle}
                {...register("date_of_birth", {
                  required: "Date of birth is required.",
                  validate: (value) => isAtLeast18(value) || "You must be at least 18 years old to apply.",
                })}
              />
            </Field>
            <Field label="Country" required error={errors.country?.message}>
              <select
                className={inputClass(errors.country)}
                style={ringStyle}
                {...register("country", {
                  required: "Country is required.",
                  onChange: (e) => {
                    const match = COUNTRIES.find((c) => c.name === e.target.value);
                    if (match) setValue("dial_code", match.dial);
                  },
                })}
              >
                <option value="">Select country</option>
                {COUNTRIES.map((c) => (
                  <option key={c.name} value={c.name}>{c.name}</option>
                ))}
              </select>
            </Field>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Email address" required error={errors.email?.message}>
              <input
                type="email"
                className={inputClass(errors.email)}
                style={ringStyle}
                placeholder="name@example.com"
                {...register("email", {
                  required: "Email is required.",
                  pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: "Enter a valid email address." },
                })}
              />
            </Field>
            <Field label="Mobile number" required error={errors.mobile_number?.message}>
              <div className="flex gap-2">
                <select className={`${inputClass(false)} !w-24`} style={ringStyle} aria-label="Country code" {...register("dial_code")}>
                  {DIAL_CODES.map((d) => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
                <input
                  type="tel"
                  className={inputClass(errors.mobile_number)}
                  style={ringStyle}
                  placeholder="Mobile number"
                  {...filteredReg("mobile_number", clean.digits, 15, {
                    required: "Mobile number is required.",
                    validate: (v) => {
                      const digits = v.replace(/\D/g, "");
                      const total = getValues("dial_code").replace(/\D/g, "").length + digits.length;
                      return (digits.length >= 4 && total >= 7 && total <= 15) || "Enter a valid mobile number (7-15 digits including country code).";
                    },
                  })}
                />
              </div>
            </Field>
          </div>

          <Field label="Primary role">
            <div className="flex flex-wrap gap-2">
              <Controller
                name="primary_role"
                control={control}
                render={({ field }) => (
                  <>
                    {ROLES.map((r) => (
                      <Chip key={r.value} selected={field.value === r.value} onClick={() => field.onChange(r.value)}>
                        {r.label}
                      </Chip>
                    ))}
                  </>
                )}
              />
            </div>
          </Field>

          <Field label="Genres" hint="Choose up to 8.">
            <div className="flex flex-wrap gap-2">
              {GENRES.map((g) => (
                <Chip key={g} selected={genres.includes(g)} onClick={() => toggleGenre(g)}>
                  {g}
                </Chip>
              ))}
            </div>
          </Field>

          <Field label="Link to an existing profile" error={errors.profile_link?.message}>
            <input
              type="url"
              className={inputClass(errors.profile_link)}
              style={ringStyle}
              placeholder="https://open.spotify.com/artist/..."
              {...register("profile_link", {
                validate: (v) => !v || isUrl(v.trim()) || "Enter a link starting with http:// or https://.",
              })}
            />
          </Field>

          <Field label="Short bio" error={errors.bio?.message}>
            <textarea
              rows={4}
              className={inputClass(errors.bio)}
              style={ringStyle}
              placeholder="Tell listeners about your sound"
              {...register("bio", { maxLength: { value: 1000, message: "Bio must be at most 1000 characters." } })}
            />
          </Field>
        </div>

        {/* ---------- Step 2: Verification ---------- */}
        <div className={step === 1 ? "space-y-5" : "hidden"}>
          <p className="text-gray-500">
            Prove you control this identity by connecting an existing profile, or enter a distributor reference.
          </p>
          <div className="space-y-3">
            {VERIFICATION_PROVIDERS.map(([key, title, subtitle, placeholder]) => (
              <ProviderCard
                key={key}
                title={title}
                subtitle={subtitle}
                placeholder={placeholder}
                value={values[key]}
                error={errors[key]}
                registration={register(key, {
                  validate: (v) => !v || isUrl(v.trim()) || "Enter a valid link starting with http:// or https://.",
                  onChange: () => clearErrors("verification"),
                })}
              />
            ))}
          </div>
          <div className="flex items-center gap-3 text-gray-400 text-xs font-semibold tracking-wide">
            <div className="flex-1 border-t border-gray-200" /> OR <div className="flex-1 border-t border-gray-200" />
          </div>
          <Field label="Distributor reference code" error={errors.distributor_reference?.message}>
            <input
              type="text"
              className={inputClass(false)}
              style={ringStyle}
              placeholder="e.g. DistroKid, CD Baby release ID"
              onKeyDown={(e) => { if (e.key === " ") e.preventDefault(); }} // a reference has no spaces: the key is not accepted
              {...register("distributor_reference", {
                maxLength: { value: 100, message: "Reference is too long." },
                validate: (v) => !/\s/.test((v || "").trim()) || "Reference cannot contain spaces.", // a pasted value with spaces inside
                onChange: () => clearErrors("verification"),
              })}
            />
          </Field>
          {errors.verification && <p role="alert" className="text-red-600 text-sm">{errors.verification.message}</p>}
        </div>

        {/* ---------- Step 3: Payout setup ---------- */}
        <div className={step === 2 ? "space-y-5" : "hidden"}>
          <Field label="Legal name" error={errors.legal_name?.message}>
            <input
              type="text"
              className={inputClass(errors.legal_name)}
              style={ringStyle}
              placeholder="As it appears on your ID"
              {...filteredReg("legal_name", clean.name, 150, {
                required: "Legal name is required.",
                minLength: { value: 2, message: "Legal name must be at least 2 characters." },
              })}
            />
          </Field>

          <Field label="Identity verification" required hint="The name on your ID must match your legal name.">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <select
                  className={inputClass(errors.id_type)}
                  style={ringStyle}
                  aria-label="ID type"
                  {...register("id_type", {
                    required: "Select an ID type.",
                    onChange: () => trigger("id_back"),
                  })}
                >
                  <option value="">Select ID type</option>
                  {ID_TYPE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                {errors.id_type && <p className="text-red-600 text-sm mt-1">{errors.id_type.message}</p>}
              </div>
              <Controller
                name="id_front"
                control={control}
                rules={{ validate: (v) => !!v || "Upload your identity document." }}
                render={({ field }) => (
                  <DocumentSlot
                    label=""
                    hint={idInfo && !idInfo.twoSided ? "Upload your ID (JPG, PNG or PDF)" : idInfo ? "Upload the front (JPG, PNG or PDF)" : "Upload front and back (JPG, PNG or PDF)"}
                    value={field.value}
                    error={errors.id_front?.message}
                    otherHashes={hashesExcept(values.id_back)}
                    onChange={(v) => {
                      field.onChange(v);
                      if (v) clearErrors("id_front");
                    }}
                  />
                )}
              />
              {idInfo?.twoSided && (
                <div className="md:col-start-2">
                  <Controller
                    name="id_back"
                    control={control}
                    rules={{ validate: (v) => !idTypeInfo(getValues("id_type"))?.twoSided || !!v || "Upload the back of your ID." }}
                    render={({ field }) => (
                      <DocumentSlot
                        label=""
                        hint="Upload the back (JPG, PNG or PDF)"
                        value={field.value}
                        error={errors.id_back?.message}
                        otherHashes={hashesExcept(values.id_front)}
                        onChange={(v) => {
                          field.onChange(v);
                          if (v) clearErrors("id_back");
                        }}
                      />
                    )}
                  />
                </div>
              )}
            </div>
          </Field>

          <Field label="Tax form">
            <div className="flex flex-wrap gap-2">
              <Controller
                name="tax_form"
                control={control}
                render={({ field }) => (
                  <>
                    <Chip selected={field.value === "w9"} onClick={() => field.onChange("w9")}>W-9 — U.S. person</Chip>
                    <Chip selected={field.value === "w8ben"} onClick={() => field.onChange("w8ben")}>W-8BEN — Non-U.S. person</Chip>
                    <Chip selected={field.value === "w8bene"} onClick={() => field.onChange("w8bene")}>W-8BEN-E — Non-U.S. company</Chip>
                  </>
                )}
              />
            </div>
          </Field>

          <Field label="Payout method">
            <div className="flex flex-wrap gap-2">
              <Controller
                name="payout_method"
                control={control}
                render={({ field }) => (
                  <>
                    <Chip
                      selected={field.value === "bank_transfer"}
                      onClick={() => {
                        if (field.value === "bank_transfer") return;
                        field.onChange("bank_transfer");
                        setValue("payout_account", "");
                        clearErrors("payout_account");
                      }}
                    >
                      Bank transfer
                    </Chip>
                    <Chip
                      selected={field.value === "paypal"}
                      onClick={() => {
                        if (field.value === "paypal") return;
                        field.onChange("paypal");
                        setValue("payout_account", "");
                        clearErrors("payout_account");
                      }}
                    >
                      PayPal
                    </Chip>
                  </>
                )}
              />
            </div>
          </Field>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field error={errors.account_holder_name?.message}>
              <input
                type="text"
                className={inputClass(errors.account_holder_name)}
                style={ringStyle}
                placeholder="Account holder name"
                aria-label="Account holder name"
                {...filteredReg("account_holder_name", clean.name, 150, { validate: (v) => ((v || "").trim().length >= 2 ? true : "Enter the account holder's full name (letters only).") })}
              />
            </Field>
            <Field error={errors.payout_account?.message} hint={initial ? "For your security, re-enter your payout details." : undefined}>
              {values.payout_method === "paypal" ? (
                <input
                  key="paypal-account"
                  type="email"
                  autoComplete="off"
                  maxLength={150}
                  className={inputClass(errors.payout_account)}
                  style={ringStyle}
                  placeholder="PayPal email"
                  aria-label="PayPal email"
                  {...register("payout_account", {
                    validate: (v) => {
                      const value = (v || "").trim();
                      if (!value) return "PayPal email is required.";
                      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) || "Enter a valid PayPal email address.";
                    },
                  })}
                />
              ) : (
                <input
                  key="bank-account"
                  type="text"
                  autoComplete="off"
                  maxLength={34}
                  className={inputClass(errors.payout_account)}
                  style={ringStyle}
                  placeholder="Account / IBAN number"
                  aria-label="Account / IBAN number"
                  {...filteredReg("payout_account", (v) => String(v || "").replace(/[^A-Za-z0-9]/g, ""), 34, {
                    validate: (v) => {
                      const value = (v || "").trim();
                      if (!value) return "Account / IBAN number is required.";
                      return /^[A-Za-z0-9]{4,34}$/.test(value) || "Account / IBAN number must be 4 to 34 letters or digits.";
                    },
                  })}
                />
              )}
            </Field>
          </div>

          <div>
            <label className="flex items-start gap-2 text-sm text-gray-700">
              <input type="checkbox" className="mt-1" {...register("age_verified", { required: "You must confirm you are 18 years of age or older." })} />
              I confirm I am 18 years of age or older
            </label>
            {errors.age_verified && <p className="text-red-600 text-sm mt-1">{errors.age_verified.message}</p>}
          </div>
          <div>
            <label className="flex items-start gap-2 text-sm text-gray-700">
              <input type="checkbox" className="mt-1" {...register("terms_accepted", { required: "You must agree to the terms to continue." })} />
              <span>
                I agree to the <span style={{ color: ACCENT }} className="font-medium">Artist Terms &amp; Sole-Ownership Warranty</span> and
                the <span style={{ color: ACCENT }} className="font-medium">Music Licensing Terms</span>
              </span>
            </label>
            {errors.terms_accepted && <p className="text-red-600 text-sm mt-1">{errors.terms_accepted.message}</p>}
          </div>
        </div>

        {anyErrors && errorStepIndex !== step && (
          <p className="text-red-600 text-sm">
            Please fix the errors on the {STEPS[errorStepIndex]} step.{" "}
            <button type="button" className="underline font-semibold" onClick={() => goTo(errorStepIndex)}>
              Go there
            </button>
          </p>
        )}

        <div className="flex items-center justify-between gap-3 pt-2">
          {step > 0 ? (
            <SecondaryButton onClick={() => goTo(step - 1)} disabled={submitting}>Back</SecondaryButton>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-3">
            {onCancel && (
              <SecondaryButton className="!px-8" onClick={onCancel} disabled={submitting}>Cancel</SecondaryButton>
            )}
            {step < LAST_STEP ? (
              <PrimaryButton onClick={goNext} disabled={currentStepHasErrors}>Next</PrimaryButton>
            ) : (
              <PrimaryButton onClick={requestSubmit} disabled={submitting}>
                Submit Application
              </PrimaryButton>
            )}
          </div>
        </div>
      </form>

      <ConfirmDialog
        open={confirmOpen}
        busy={submitting}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={handleSubmit(onSubmit, onInvalid)}
      />
    </div>
  );
}
